/**
 * Prolonged Field Care / Prolonged Casualty Care Constants - PSP/CRE Tool v3.0
 *
 * Models the survival impact of extended pre-hospital care when evacuation is
 * delayed or denied. Applies automatically when golden hour is not achievable —
 * it is never a planner-set primary input (see Doctrinal Note below).
 *
 * @version 1.0.0
 * @date March 10, 2026
 * @category Cat-23 Research Inventory Audit — Prolonged Field Care (PFC)
 *
 * VERSION 1.0.0 (March 10, 2026) — ENH-023: Initial implementation.
 *   Category 23 of §20 Research Inventory Audit: was "50% — constants designed,
 *   zero calculator integration". This file replaces the previously planned
 *   prolongedFieldCare.js (referenced in roadmap v1.2 but never wired).
 *
 * ============================================================================
 * DOCTRINAL BASIS
 * ============================================================================
 *
 * ORIGIN:
 *   PFC was conceptualized in 2013 by Dr. Sean Keenan (10th Special Forces Group)
 *   for austere SOF operations in US Africa Command. It was formalized as US DoD
 *   doctrine via Joint Trauma System Prolonged Casualty Care Guidelines (Dec 2021)
 *   and CoTCCC Position Statement on PCC (May 2024, CoTCCC = Committee on Tactical
 *   Combat Casualty Care).
 *
 * TERMINOLOGY:
 *   PFC (Prolonged Field Care) — original SOF term, still widely used.
 *   PCC (Prolonged Casualty Care) — current DoD preferred term. PCC = PFC
 *   generalized to all Role 1 responders across the full range of military
 *   operations. The tool uses PFC internally as that is what NATO-adjacent
 *   literature and training programs (including Ukraine) use.
 *
 * NATO STATUS:
 *   No AJMedP or STANAG formally adopts PFC/PCC by name (as of March 2026).
 *   AJP-4.10 and AJMedP-1 define the Roles of Care system but do not codify PCC
 *   as a planning standard. Adoption is rapid but not yet formalized in NATO
 *   standardization documents. US, UK, and Baltic nations are operationally
 *   implementing PCC doctrine driven by Ukraine 2022-2024 lessons.
 *
 * PLANNING RULE (critical for correct tool behavior):
 *   Per JTS PCC Guidelines: "Operational and medical planning should seek to AVOID
 *   categorizing PCC as a primary medical support capability or control factor
 *   during deliberate risk assessment; however, an effective medical plan always
 *   includes PCC as a CONTINGENCY."
 *   → PFC is therefore NEVER a user-set input in this tool.
 *   → PFC conditions are detected automatically (golden hour not achievable)
 *     and the DOW penalty is applied based on the force's medical capability.
 *
 * RELATIONSHIP TO TCCC AND CLS:
 *   CLS (Combat Lifesaver, 0–15 min): tourniquet, wound packing, buddy aid.
 *     Performed by soldiers. Modeled via cls_training_percentage → DOW modifier.
 *   TCCC (0–60 min): combat medic arrives, stabilizes, evacuates.
 *     Golden hour target. Already tracked (golden_hour_compliant).
 *   PFC/PCC (>60 min): evacuation delayed/denied. Medic must extend care.
 *     THIS FILE. Activated when golden_hour_compliant = false.
 *   These are sequential — PFC begins where TCCC ends. CLS ≠ PFC.
 *
 * SOURCES:
 *   - JTS Prolonged Casualty Care Guidelines v2.0 (Dec 2021, ID-91)
 *   - CoTCCC Position Statement on PCC (May 2024)
 *   - DoD Instruction 1322.24, Medical Readiness Training
 *   - Ukraine 2022-2024 ISW / JHU APL / International Legion Medical Service data
 *   - Borden Institute "Death of the Golden Hour" (July 2025)
 *   - Dilday et al, "'Golden day' is a myth", BMJ Military Health (2024)
 *   - French Army "Prolonged Casualty Care during Serval and Barkhane" (2023)
 *   - Keenan et al, Annals of Surgery 279(1) (2024)
 */

// ============================================================================
// PFC CAPABILITY LEVELS
// Derived automatically from medical_capability (Tab1 Force Quality).
// Higher medical capability = better PFC-trained medics = better DOW outcomes
// when evacuation is delayed.
// ============================================================================

/**
 * PFC capability bands mapped to force medical_capability levels.
 * Each band represents what the battalion combat medic (Role 1) can sustain
 * when evacuation to Role 2 is denied or delayed beyond 60 minutes.
 *
 * dow_survival_modifier: multiplier applied to the DOW rate when PFC conditions
 *   exist (golden hour not achieved). Values >1.0 = MORE died of wounds (worse capability).
 *   Applied only when golden_hour_compliant = false.
 *   2026-10-19 (R1-06, Dr. Tatar): the values were 0.70 / 0.88 / 0.95 / 0.98 and were read as a DOW multiplier, so the WORST capability
 *   gave the fewest deaths (Poor 7.1 percent against Excellent 10.0 percent mean daily rate in the probe), against the warnings below
 *   (+30 percent, +12 percent). Now 1.30 / 1.12 / 1.05 / 1.02. STATUS: EDUCATED GUESS (same magnitudes as before, direction corrected);
 *   the redesign where PFC acts on the length of the wait is R2-21.
 *
 * 2026-10-19: the figures below (35 percent, median 8 hours, survival 68 against 31 percent) could NOT be found in any source (docs/research/RESEARCH_SURVIVAL_VS_TIME.md):
 * UNVERIFIED, not to be used as an anchor.
 * Ukraine data (ISW / JHU APL, 2022-2024):
 *   35% of casualties experienced PFC conditions (median duration: 8 hours)
 *   Survival WITH capable PFC: ~68%
 *   Survival WITHOUT PFC capability: ~31%
 *   Ratio = 2.2× survival difference depending on medical capability.
 */
// HOUSEKEEPING (Dr. Tatar 2026-10-25): REMOVE the flat died-of-wounds multiplier dow_survival_modifier below, getPFCModifier() and its use in
// casualtyCalculator.js (_pfcFlatModifier / pfcAdjustedDOW). It acts only when the wound hazard model is not ON (older modes); the prolonged field care
// benefit of the current model is the offset on the wait (PFC_WAIT_OFFSET, evacuationWindowModel.js) with max_hold_hours. Keep max_hold_hours and the labels.
// Listed in docs/DEAD_CODE_REGISTER.md.
export const PFC_CAPABILITY = {
  NONE: {
    label: 'No PFC Capability',
    description: 'Medics have TCCC only; no extended monitoring, no IV fluids, no blood products',
    dow_survival_modifier: 1.30,   // +30% died of wounds: medic can only hold, not treat
    max_hold_hours: 1,
    medical_capability_keys: ['POOR', 'COLLAPSED'],
    note: 'Soviet/legacy-trained units without PFC curriculum'
  },
  BASIC: {
    label: 'Basic PFC',
    description: 'Extended TCCC with monitoring and IV fluids; limited blood products',
    dow_survival_modifier: 1.12,   // +12%
    max_hold_hours: 4,
    medical_capability_keys: ['ADEQUATE', 'LIMITED'],
    note: 'NATO minimum standard; tourniquet conversion, basic resuscitation'
  },
  INTERMEDIATE: {
    label: 'Intermediate PFC',
    description: 'Full PFC with blood products, advanced airway, antibiotics, serial assessment',
    dow_survival_modifier: 1.05,   // +5%
    max_hold_hours: 12,
    medical_capability_keys: ['GOOD'],
    note: 'NATO standard (AJP-4.10 compliant); walking blood bank capable'
  },
  ADVANCED: {
    label: 'Advanced PFC (DCR-capable)',
    description: 'Damage Control Resuscitation at Role 1; blood products, monitoring up to 24h',
    dow_survival_modifier: 1.02,   // +2%: near-optimal for field conditions
    max_hold_hours: 24,
    medical_capability_keys: ['EXCELLENT'],
    note: 'US/UK Special Operations standard; conventional forces building toward this'
  }
};

/**
 * Modifier when golden hour IS achieved (PFC not triggered).
 * Baseline — no penalty applied.
 */
export const PFC_NOT_TRIGGERED_MODIFIER = 1.0;

// ============================================================================
// MAPPING: medical_capability → PFC capability level
// ============================================================================

/**
 * Maps the existing medical_capability field (from combatEffectivenessModifiers.js)
 * to the appropriate PFC capability level.
 * Used by getPFCModifier() to auto-derive without any new user input.
 */
export const MEDICAL_CAPABILITY_TO_PFC = {
  COLLAPSED: 'NONE',        // 2026-10-19: Tab 1 offers six levels; COLLAPSED and LIMITED had no entry and fell to BASIC (PLACEHOLDER mapping)
  POOR:      'NONE',
  LIMITED:   'BASIC',
  ADEQUATE:  'BASIC',
  GOOD:      'INTERMEDIATE',
  EXCELLENT: 'ADVANCED'
};

// ============================================================================
// HELPER: getPFCModifier
// ============================================================================

/**
 * Calculate the PFC DOW survival modifier for a given scenario.
 *
 * Called ONLY when golden_hour_compliant = false.
 * When golden hour is achieved, returns modifier = 1.0 (no PFC penalty).
 *
 * @param {boolean} goldenHourCompliant   - true if evac time ≤ 60 min
 * @param {string}  medicalCapability     - 'POOR'|'ADEQUATE'|'GOOD'|'EXCELLENT'
 * @returns {{
 *   modifier: number,        - DOW survival multiplier (0.70–1.0)
 *   triggered: boolean,      - true when PFC conditions exist
 *   pfc_level: string,       - PFC capability key or 'NOT_TRIGGERED'
 *   label: string,           - Display label
 *   description: string,     - Explanation for reports
 *   warning: string|null     - Warning text for planner
 * }}
 */
export function getPFCModifier(goldenHourCompliant, medicalCapability, pfcKeyOverride = null) {
  // PFC not triggered — golden hour achieved
  if (goldenHourCompliant) {
    return {
      modifier: PFC_NOT_TRIGGERED_MODIFIER,
      triggered: false,
      pfc_level: 'NOT_TRIGGERED',
      label: 'Golden Hour Achieved',
      description: 'Evacuation within 60 minutes — PFC not required',
      warning: null
    };
  }

  // PFC triggered — derive capability from medical_capability
  const pfcKey = pfcKeyOverride || MEDICAL_CAPABILITY_TO_PFC[medicalCapability] || 'BASIC';   // R1-07: the override is the level derived from Tab 3 (pfcFromFacilities.js)
  const pfcBand = PFC_CAPABILITY[pfcKey];

  return {
    modifier: pfcBand.dow_survival_modifier,
    triggered: true,
    pfc_level: pfcKey,
    label: pfcBand.label,
    description: pfcBand.description,
    warning: pfcKey === 'NONE'
      ? 'CRITICAL: No PFC capability — delayed evacuation will significantly increase DOW (+30%)'
      : pfcKey === 'BASIC'
      ? 'PFC conditions exist — basic capability only; DOW risk elevated (+12%)'
      : null
  };
}

// ============================================================================
// INFORMATIONAL: PFC TRIGGER CONDITIONS (for reports and documentation)
// Not used in calculation — already captured by golden_hour_compliant flag.
// ============================================================================

/**
 * Conditions that force PFC (informational — triggers are already modeled
 * via threat level, route status, and evacuation time calculations).
 * Included here for educational display in Tab5 reports.
 */
export const PFC_TRIGGER_CONDITIONS = {
  CONTESTED_AIRSPACE: {
    label: 'Contested Airspace',
    description: 'Helicopter MEDEVAC cannot fly — ground evacuation only',
    typical_delay_hours: { min: 2, max: 12 }
  },
  MASS_CASUALTY: {
    label: 'Mass Casualty Event',
    description: 'MTF overwhelmed — casualties must be held at Role 1',
    typical_delay_hours: { min: 1, max: 6 }
  },
  ROUTE_CONTESTED: {
    label: 'Contested Ground Routes',
    description: 'Evacuation route under fire or damaged — movement delayed',
    typical_delay_hours: { min: 1, max: 8 }
  },
  ENCIRCLEMENT: {
    label: 'Unit Encircled',
    description: 'No evacuation route available — sustained PFC required',
    typical_delay_hours: { min: 6, max: 72 }
  },
  WEATHER: {
    label: 'Adverse Weather',
    description: 'Helicopter MEDEVAC grounded — ground evac only',
    typical_delay_hours: { min: 2, max: 24 }
  }
};

/**
 * Ukraine 2022-2024 PFC statistics (ISW / JHU APL / International Legion data).
 * Used in Tab5 report contextual notes.
 */
export const PFC_UKRAINE_STATISTICS = {
  pfc_required_rate: 0.35,          // 35% of casualties experienced PFC conditions
  median_duration_hours: 8,
  pfc_over_24h_rate: 0.12,          // 12% held > 24 hours
  survival_with_pfc_capability: 0.68,
  survival_without_pfc_capability: 0.31,
  tourniquet_beyond_2h_rate: 0.75,  // 75% of tourniquets applied without indication or beyond safe window
  source: 'ISW / JHU APL / International Legion Medical Service 2022-2024'
};
